import React, { useMemo, useState } from 'react';
import { Alert, Button, Flex, Form, Input, List, Select, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ChannelImportDraftEntity,
  ChannelImportDraftStatus,
  MAX_CHANNEL_IMPORT_LINKS,
  extractChannelImportLinks,
} from '../../../domain/entities/ChannelImportDraft';
import { TelegramAccountEntity } from '../../../domain/entities/TelegramAccount';

interface ChannelImportLinksFormProps {
  accounts: TelegramAccountEntity[];
  recent: ChannelImportDraftEntity[];
  isSaving: boolean;
  onStart: (rawLinks: string, telegramAccountUuid: string) => void;
  onOpenDraft: (uuid: string) => void;
}

const STATUS_COLORS: Record<ChannelImportDraftStatus, string> = {
  resolving: 'processing',
  ready: 'blue',
  imported: 'green',
  failed: 'red',
};

const ChannelImportLinksForm: React.FC<ChannelImportLinksFormProps> = ({ accounts, recent, isSaving, onStart, onOpenDraft }) => {
  const { t, i18n } = useTranslation();
  const [rawLinks, setRawLinks] = useState('');
  const [accountUuid, setAccountUuid] = useState<string | undefined>();

  const linesCount = useMemo(() => extractChannelImportLinks(rawLinks).length, [rawLinks]);
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }), [i18n.language]);
  const selectedAccount = accountUuid ?? (accounts.length === 1 ? accounts[0].uuid : undefined);

  const statusLabel = (status: ChannelImportDraftStatus) => t(`channel-import-status-${status}`, { ns: 'labels' });

  return (
    <Flex vertical gap={24}>
      <Form layout="vertical" onFinish={() => selectedAccount && onStart(rawLinks, selectedAccount)}>
        <Form.Item
          label={t('channel-import-links', { ns: 'labels' })}
          extra={t('channel-import-links-hint', { ns: 'labels', count: linesCount, max: MAX_CHANNEL_IMPORT_LINKS })}
        >
          <Input.TextArea
            value={rawLinks}
            onChange={(e) => setRawLinks(e.target.value)}
            autoSize={{ minRows: 8, maxRows: 20 }}
            placeholder={t('channel-import-links-placeholder', { ns: 'labels' })}
          />
        </Form.Item>
        <Form.Item
          label={t('channel-import-telegram-account', { ns: 'labels' })}
          extra={t('channel-import-telegram-account-hint', { ns: 'labels' })}
        >
          <Select
            value={selectedAccount}
            onChange={setAccountUuid}
            placeholder={t('channel-import-telegram-account-placeholder', { ns: 'labels' })}
            options={accounts.map((account) => ({ value: account.uuid, label: account.phone }))}
            notFoundContent={t('no-telegram-accounts', { ns: 'labels' })}
          />
        </Form.Item>
        {accounts.length === 0 && (
          <Alert type="warning" showIcon message={t('channel-import-no-authenticated-accounts', { ns: 'labels' })} style={{ marginBottom: 16 }} />
        )}
        <Button type="primary" htmlType="submit" loading={isSaving} disabled={linesCount === 0 || !selectedAccount}>
          {t('channel-import-start', { ns: 'buttons' })}
        </Button>
      </Form>

      {recent.length > 0 && (
        <div>
          <Typography.Title level={5}>{t('channel-import-recent', { ns: 'titles' })}</Typography.Title>
          <List
            size="small"
            bordered
            dataSource={recent}
            rowKey={(draft) => draft.uuid}
            renderItem={(draft) => (
              <List.Item actions={[<Button key="open" type="link" onClick={() => onOpenDraft(draft.uuid)}>{t('open', { ns: 'buttons' })}</Button>]}>
                <Flex gap={12} align="center" wrap="wrap">
                  <Typography.Text>{dateFormat.format(new Date(draft.createdAt))}</Typography.Text>
                  <Tag color={STATUS_COLORS[draft.status]}>{statusLabel(draft.status)}</Tag>
                  <Typography.Text type="secondary">{t('channel-import-links-count', { ns: 'labels', count: draft.totalLinks })}</Typography.Text>
                </Flex>
              </List.Item>
            )}
          />
        </div>
      )}
    </Flex>
  );
};

export default ChannelImportLinksForm;
